// src/components/admin/scheduling/ActivityLogView.js
import React, { useEffect, useState, useCallback } from "react";
import "./ActivityLogView.css";
import { backdropDismiss } from "../../../utils/backdropDismiss";
import {
  fetchActivityLogs,
  deleteLogItem,
  clearAllLogs,
  purgeOldLogs,
  getLoggingEnabledForCurrentUser,
  setLoggingEnabledForCurrentUser,
} from "./loggingApi";

// ── Human-readable sentence builder ──────────────────────────────────────────
function buildSentence(log) {
  const m       = log.meta || {};
  const student = m.studentName || "a student";
  const tutor   = m.tutorName   || "";
  const context = m.sessionContext || "";
  const from    = (m.valFrom || "").trim();
  const to      = (m.valTo   || "").trim();
  const type    = (log.actionType || "").toUpperCase();

  const S   = <strong key="s" className="alv-nameStudent">{student}</strong>;
  const ctx = context ? <span key="ctx" className="alv-ctx"> — {context}</span> : null;

  switch (type) {
    case "CREATE_SESSION":
      return (
        <>
          Session created for {S}
          {tutor ? <> with <strong key="t">{tutor}</strong></> : ""}
          {ctx}
        </>
      );

    case "MOVE_SESSION":
      return (
        <>
          Session moved for {S}
          {from && to ? <> from <em>{from}</em> to <em>{to}</em></> : ""}
          {ctx}
        </>
      );

    case "STATUS_CHANGE":
      return (
        <>
          Status changed for {S}
          {from && to ? <> from <em>{from}</em> to <em>{to}</em></> : ""}
          {ctx}
        </>
      );

    case "TUTOR_CHANGE":
      return (
        <>
          Tutor changed for {S}
          {from && to
            ? <> from <strong key="tf">{from}</strong> to <strong key="tt">{to}</strong></>
            : tutor ? <> to <strong key="tt">{tutor}</strong></> : ""}
          {ctx}
        </>
      );

    case "SUBJECT_CHANGE":
      return (
        <>
          Subject changed for {S}
          {from && to ? <> from <em>{from}</em> to <em>{to}</em></> : ""}
          {ctx}
        </>
      );

    case "MULTI_UPDATE": {
      // Extract field labels from "Label: OldValue" lines in valFrom
      const labels = from
        .split("\n")
        .map((line) => {
          const ci = line.indexOf(":");
          return ci > -1 ? line.substring(0, ci).trim().toLowerCase() : null;
        })
        .filter(Boolean);
      const fieldList = labels.length > 0
        ? <span key="fl" className="alv-ctx">: {labels.join(", ")}</span>
        : null;
      return (
        <>
          Multiple fields updated for {S}{fieldList}{ctx}
        </>
      );
    }

    case "DELETE_SESSION":
      return <>Session deleted for {S}{ctx}</>;

    case "RESTORE_SESSION":
      return <>Session restored for {S}{ctx}</>;

    // ── Events ──────────────────────────────────────────────────────────────
    case "CREATE_EVENT":
      return <>Event created: <strong key="s">{student}</strong>{ctx}</>;

    case "MOVE_EVENT":
      return (
        <>
          Event moved: <strong key="s">{student}</strong>
          {from && to ? <> — <em>{from}</em> → <em>{to}</em></> : ""}
        </>
      );

    case "UPDATE_EVENT":
      return (
        <>
          Event updated: <strong key="s">{student}</strong>
          {from ? <span key="fl" className="alv-ctx"> ({from})</span> : ""}
          {ctx}
        </>
      );

    case "DELETE_EVENT":
      return <>Event deleted: <strong key="s">{student}</strong>{ctx}</>;

    case "BATCH_UPDATE":
      return (
        <>
          Batch update applied
          {student !== "a student" ? <> for {S}</> : ""}
          {from && to && from !== "Various"
            ? <> — <em>{from}</em> → <em>{to}</em></>
            : ""}
          {ctx}
        </>
      );

    case "NOTE_CHANGE":
      return <>Note updated for {S}'s session{ctx}</>;

    case "BATCH_DELETE": {
      const lines = (context || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const preview = lines[0] || "";
      const extra = lines.length > 1 ? ` +${lines.length - 1} more` : "";
      return (
        <>
          {lines.length || 0} session{lines.length === 1 ? "" : "s"} deleted
          {preview ? (
            <span key="p" className="alv-ctx"> — {preview}{extra}</span>
          ) : null}
        </>
      );
    }

    default:
      return (
        <>
          <em>{(type || "Action").toLowerCase().replace(/_/g, " ")}</em>
          {student !== "a student" ? <> for {S}</> : ""}
          {ctx}
        </>
      );
  }
}

// ── Timestamp helpers ─────────────────────────────────────────────────────────
function formatShortTs(ts) {
  if (!ts) return "—";
  const d   = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  if (d.toDateString() === now.toDateString()) return `Today, ${time}`;

  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return `Yesterday, ${time}`;

  const date = d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
  return `${date}, ${time}`;
}

function formatFullTs(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", {
    month: "2-digit", day: "2-digit", year: "numeric",
    hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true,
  });
}

// ── Badge helpers ─────────────────────────────────────────────────────────────
function getBadgeClass(type) {
  const t = (type || "").toUpperCase();
  if (t.includes("EVENT"))   return "event";
  if (t.includes("CREATE"))  return "create";
  if (t.includes("DELETE") || t.includes("WIPE")) return "delete";
  if (t.includes("MOVE"))    return "move";
  if (t.includes("STATUS"))  return "status";
  if (t.includes("TUTOR"))   return "tutor";
  if (t.includes("SUBJECT")) return "subject";
  if (t.includes("MULTI"))   return "multi";
  if (t.includes("BATCH"))   return "batch";
  return "generic";
}

const BADGE_LABELS = {
  CREATE_SESSION:  "Created",
  MOVE_SESSION:    "Moved",
  STATUS_CHANGE:   "Status",
  TUTOR_CHANGE:    "Tutor",
  SUBJECT_CHANGE:  "Subject",
  MULTI_UPDATE:    "Multi",
  DELETE_SESSION:  "Deleted",
  RESTORE_SESSION: "Restored",
  BATCH_DELETE:    "Deleted",
  BATCH_UPDATE:    "Batch",
  NOTE_CHANGE:     "Note",
  // Events
  CREATE_EVENT:    "Event",
  MOVE_EVENT:      "Event",
  UPDATE_EVENT:    "Event",
  DELETE_EVENT:    "Event",
};

function formatBadge(type) {
  return BADGE_LABELS[type] || (type || "LOG").replace(/_/g, " ");
}

// ── Detail modal ──────────────────────────────────────────────────────────────
function LogDetailModal({ log, onClose }) {
  // Close on Escape — must be before any early return (Rules of Hooks)
  useEffect(() => {
    if (!log) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [log, onClose]);

  if (!log) return null;
  const m = log.meta || {};

  const rows = [
    { label: "Timestamp", value: formatFullTs(log.timestamp) },
    { label: "Admin",     value: m.adminName   || "—" },
    { label: "Student",   value: m.studentName || "—" },
    { label: "Tutor",     value: m.tutorName   || "—" },
    { label: "Action",    value: log.actionType || "—" },
    { label: "Context",   value: m.sessionContext || "—", pre: (m.sessionContext || "").includes("\n") },
    { label: "From",      value: m.valFrom || "—", pre: true },
    { label: "To",        value: m.valTo   || "—", pre: true },
  ];

  return (
    <div className="alv-overlay" {...backdropDismiss(onClose)}>
      <div className="alv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="alv-modalHeader">
          <span className={`alv-badge ${getBadgeClass(log.actionType)}`}>
            {formatBadge(log.actionType)}
          </span>
          <span className="alv-modalTitle">Log Detail</span>
          <button className="alv-modalClose" onClick={onClose}>✕</button>
        </div>

        <div className="alv-modalBody">
          {rows.map((r) => (
            <div key={r.label} className="alv-modalRow">
              <div className="alv-modalLabel">{r.label}</div>
              <div className={`alv-modalValue${r.pre ? " alv-modalValuePre" : ""}`}>
                {r.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ActivityLogView() {
  const [logs,    setLogs]    = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [filterAction, setFilterAction] = useState("ALL");
  const [searchTerm,   setSearchTerm]   = useState("");
  const [selectedLog,  setSelectedLog]  = useState(null);

  // Developer mode — hold Option / Alt for 2 s
  const [canDelete,       setCanDelete]       = useState(false);
  const [loggingEnabled,  setLoggingEnabled]  = useState(true);
  const [selectedIds,     setSelectedIds]     = useState(new Set());

  useEffect(() => {
    let timer;
    const down = (e) => {
      if (e.key === "Alt" && !e.repeat) timer = setTimeout(() => setCanDelete(true), 2000);
    };
    const up = (e) => {
      if (e.key === "Alt") { clearTimeout(timer); setCanDelete(false); }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup",   up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup",   up);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (canDelete) setLoggingEnabled(getLoggingEnabledForCurrentUser());
    else           setSelectedIds(new Set());
  }, [canDelete]);

  // Auto-purge on mount
  useEffect(() => {
    purgeOldLogs().catch((e) => console.error("Auto-purge failed", e));
  }, []);

  // Fetch
  const loadLogs = useCallback(
    async (isRefresh = false) => {
      if (loading) return;
      setLoading(true);
      try {
        const cursor  = isRefresh ? null : lastDoc;
        const filters = filterAction !== "ALL" ? { actionType: filterAction } : {};
        const { logs: fresh, lastVisible } = await fetchActivityLogs(cursor, 50, filters);
        setLogs((prev) => (isRefresh ? fresh : [...prev, ...fresh]));
        setLastDoc(lastVisible);
        setHasMore(fresh.length === 50);
      } catch (err) {
        console.error("Failed to load logs", err);
      } finally {
        setLoading(false);
      }
    },
    [lastDoc, loading, filterAction]
  );

  // Reload on filter change
  useEffect(() => {
    setLastDoc(null);
    setHasMore(true);
    setLogs([]);
    const run = async () => {
      setLoading(true);
      try {
        const filters = filterAction !== "ALL" ? { actionType: filterAction } : {};
        const { logs: fresh, lastVisible } = await fetchActivityLogs(null, 50, filters);
        setLogs(fresh);
        setLastDoc(lastVisible);
        setHasMore(fresh.length === 50);
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction]);

  // Developer actions
  const handleClearAll = async () => {
    if (!window.confirm("ARE YOU SURE? This will wipe ALL logs.")) return;
    setLoading(true);
    await clearAllLogs();
    setLogs([]);
    setLastDoc(null);
    setHasMore(false);
    setLoading(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const count = selectedIds.size;
    if (!count) return;
    if (!window.confirm(`Permanently delete ${count} selected log${count > 1 ? "s" : ""}?`)) return;
    setLoading(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => deleteLogItem(id)));
      setLogs((prev) => prev.filter((l) => !selectedIds.has(l.id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Batch delete failed", err);
      alert("Some logs could not be deleted.");
    } finally {
      setLoading(false);
    }
  };

  // Client-side search across key fields
  const visibleLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const m = log.meta || {};
    return [
      m.adminName, m.studentName, m.tutorName,
      m.sessionContext, m.valFrom, m.valTo,
      log.actionType, log.description,
    ].some((f) => f && f.toLowerCase().includes(term));
  });

  // Row click: dev-mode selects; normal mode opens modal
  const handleRowClick = (log) => {
    if (canDelete) toggleSelect(log.id);
    else setSelectedLog(log);
  };

  return (
    <div className="alv-page">

      {/* ── Header ── */}
      <div className="alv-pageHeader">
        <div className="alv-pageTitle">Activity Logs</div>
        <div className="alv-pageSubtitle">Auto-purged after 14 days</div>
      </div>

      {/* ── Controls ── */}
      <div className="alv-controlsCard">
        <div className="alv-controlsRow">
          <input
            type="text"
            placeholder="Search by name, action, or context…"
            className="alv-searchInput"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <select
            className="alv-filterSelect"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
          >
            <option value="ALL">All Actions</option>
            <option value="CREATE_SESSION">Create Session</option>
            <option value="MOVE_SESSION">Move / Edit Time</option>
            <option value="STATUS_CHANGE">Status Change</option>
            <option value="TUTOR_CHANGE">Tutor Change</option>
            <option value="SUBJECT_CHANGE">Subject Change</option>
            <option value="MULTI_UPDATE">Multi-Field Edit</option>
            <option value="DELETE_SESSION">Delete Session</option>
            <option value="RESTORE_SESSION">Restore Session</option>
            <option value="BATCH_DELETE">Batch Delete</option>
            <option value="BATCH_UPDATE">Batch Update</option>
            <optgroup label="──────────" disabled />
            <option value="CREATE_EVENT">Create Event</option>
            <option value="MOVE_EVENT">Move Event</option>
            <option value="UPDATE_EVENT">Update Event</option>
            <option value="DELETE_EVENT">Delete Event</option>
          </select>

          <button className="alv-btn" onClick={() => loadLogs(true)}>
            Refresh
          </button>

          {/* Developer-mode controls */}
          {canDelete && selectedIds.size > 0 && (
            <button className="alv-btn alv-btnDanger" onClick={handleDeleteSelected}>
              Delete Selected ({selectedIds.size})
            </button>
          )}
          {canDelete && (
            <label
              className="alv-devToggle"
              title="Temporarily disable activity logging for this admin account"
            >
              <span>Logging</span>
              <input
                type="checkbox"
                checked={loggingEnabled}
                onChange={(e) => {
                  const v = e.target.checked;
                  setLoggingEnabled(v);
                  setLoggingEnabledForCurrentUser(v);
                }}
              />
            </label>
          )}
          {canDelete && (
            <button className="alv-btn alv-btnDanger" onClick={handleClearAll}>
              Empty Logs
            </button>
          )}
        </div>
      </div>

      {/* ── Activity feed ── */}
      <div className="alv-feedCard">

        {/* Empty / loading states */}
        {visibleLogs.length === 0 && loading && (
          <div className="alv-empty">Loading…</div>
        )}
        {visibleLogs.length === 0 && !loading && (
          <div className="alv-empty">
            {searchTerm ? "No logs match your search." : "No activity logs found."}
          </div>
        )}

        {/* Feed rows */}
        {visibleLogs.map((log) => {
          const isSelected = canDelete && selectedIds.has(log.id);
          return (
            <div
              key={log.id}
              className={`alv-item${isSelected ? " alv-itemSelected" : ""}`}
              onClick={() => handleRowClick(log)}
              title={canDelete ? "Click to select for deletion" : "Click to view full details"}
            >
              {/* Dev-mode checkbox indicator */}
              {canDelete && (
                <span className={`alv-checkbox${isSelected ? " alv-checkboxOn" : ""}`}>
                  {isSelected ? "✓" : ""}
                </span>
              )}

              {/* Action badge */}
              <span className={`alv-badge ${getBadgeClass(log.actionType)}`}>
                {formatBadge(log.actionType)}
              </span>

              {/* Human-readable sentence */}
              <span className="alv-sentence">{buildSentence(log)}</span>

              {/* Timestamp */}
              <span className="alv-time">{formatShortTs(log.timestamp)}</span>

              {/* "View details" hint (non-dev mode) */}
              {!canDelete && (
                <span className="alv-viewHint">Details →</span>
              )}
            </div>
          );
        })}

        {/* Load more */}
        {hasMore && !loading && (
          <div className="alv-loadMoreRow">
            <button className="alv-loadMoreBtn" onClick={() => loadLogs(false)}>
              Load more
            </button>
          </div>
        )}
        {loading && visibleLogs.length > 0 && (
          <div className="alv-loadMoreRow">
            <span className="alv-loadingLabel">Loading…</span>
          </div>
        )}
      </div>

      {/* ── Detail modal ── */}
      {selectedLog && (
        <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}

    </div>
  );
}
